import { Link } from "react-router-dom";
import { Instagram, Facebook, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import { DonateButton } from "@/components/DonateButton";
import { SOCIALS, CLUB_NAV } from "@/data/content";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

export const Footer = () => (
  <footer className="bg-teal-deep text-sand" data-testid="main-footer">
    <div className="mx-auto max-w-7xl px-6 py-16">
      <div className="grid gap-12 md:grid-cols-4">
        <div className="md:col-span-2">
          <Logo dark />
          <p className="mt-5 max-w-md text-sm leading-relaxed text-sand/60">
            A just and equitable society where every learner, regardless of postcode or background, 
            has access to the cultural capital and academic foundation they deserve.
          </p>
          <p className="mt-4 flex items-center gap-2 font-mono-brand text-xs uppercase tracking-[0.25em] text-sand/40">
            <MapPin size={13} className="text-ember" />
            Cape Town, Western Cape, Est. 2022
          </p>
          <div className="mt-6 flex gap-3">
            <a
              data-testid="social-instagram"
              href={SOCIALS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="EduPower Foundation on Instagram"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-sand/15 text-sand/70 transition-all duration-300 hover:border-ember hover:bg-ember hover:text-sand"
            >
              <Instagram size={18} />
            </a>
            <a
              data-testid="social-facebook"
              href={SOCIALS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="EduPower Foundation on Facebook"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-sand/15 text-sand/70 transition-all duration-300 hover:border-ember hover:bg-ember hover:text-sand"
            >
              <Facebook size={18} />
            </a>
            <a
              data-testid="social-tiktok"
              href={SOCIALS.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="EduPower Foundation on TikTok"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-sand/15 text-sand/70 transition-all duration-300 hover:border-ember hover:bg-ember hover:text-sand"
            >
              <TikTokIcon />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-mono-brand text-xs font-bold uppercase tracking-[0.25em] text-ember">
            English is Lekka
          </h4>
          <ul className="mt-4 space-y-2.5">
            {CLUB_NAV.map((c) => (
              <li key={c.to}>
                <Link
                  to={c.to}
                  className="text-sm text-sand/60 transition-colors hover:text-sand"
                >
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-mono-brand text-xs font-bold uppercase tracking-[0.25em] text-ember">
            Foundation
          </h4>
          <ul className="mt-4 space-y-2.5">
            <li><Link to="/about" className="text-sm text-sand/60 transition-colors hover:text-sand">About & Timeline</Link></li>
            <li><Link to="/edupower13" className="text-sm text-sand/60 transition-colors hover:text-sand">EduPower13</Link></li>
            <li><Link to="/wellbeing" className="text-sm text-sand/60 transition-colors hover:text-sand">Wellbeing</Link></li>
            <li><Link to="/team" className="text-sm text-sand/60 transition-colors hover:text-sand">Meet the Team</Link></li>
            <li><Link to="/events" className="text-sm text-sand/60 transition-colors hover:text-sand">Events & Gallery</Link></li>
          </ul>
          <div className="mt-6">
            <DonateButton testId="footer-donate-button">Donate now</DonateButton>
          </div>
        </div>
      </div>

      <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-sand/10 pt-8 sm:flex-row">
        <p className="text-xs text-sand/40">
          &copy; {new Date().getFullYear()} EduPower Foundation NPC 2024/513680/0. We believe language is power.
        </p>
        <div className="flex items-center gap-5">
          <Link to="/terms" data-testid="footer-terms-link" className="text-[11px] text-sand/40 transition-colors hover:text-sand/70">
            Terms
          </Link>
          <Link to="/privacy" data-testid="footer-privacy-link" className="text-[11px] text-sand/40 transition-colors hover:text-sand/70">
            Privacy
          </Link>
          <Link to="/admin/login" data-testid="footer-admin-link" className="text-[11px] text-sand/25 transition-colors hover:text-sand/60">
            Team login
          </Link>
        </div>
        <p className="font-mono-brand text-[11px] uppercase tracking-[0.3em] text-sand/30">
          Potential into opportunity
        </p>
      </div>
    </div>
  </footer>
);
